import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/shadcn/avatar';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/shadcn/tooltip';
import { useTranslations } from '@/hooks/useTranslations';
import { cn } from '@/lib/utils';
import { type User, type WorkOrderAssignment } from '@/types';
import { Crown, User as UserIcon } from 'lucide-react';

interface InlineAssigneesCellProps {
    assignments?: WorkOrderAssignment[];
    assignedUser?: User | null;
    className?: string;
}

export function InlineAssigneesCell({ assignments = [], assignedUser, className }: InlineAssigneesCellProps) {
    const { t } = useTranslations();
    const hasAssignments = assignments.length > 0;
    const fallbackAssignments =
        !hasAssignments && assignedUser
            ? [
                  {
                      id: 'legacy-assignee',
                      user: assignedUser,
                      role: 'lead' as const,
                      assigned_at: '',
                      assigned_by: null,
                  },
              ]
            : [];

    const displayAssignments = hasAssignments ? assignments : fallbackAssignments;

    if (displayAssignments.length === 0) {
        return (
            <div className={cn('text-muted-foreground flex items-center gap-2', className)}>
                <UserIcon className="h-4 w-4" />
                <span className="text-xs italic">{t('work_orders.assignments.unassigned', 'Unassigned')}</span>
            </div>
        );
    }

    const leadAssignment = displayAssignments.find((assignment) => assignment.role === 'lead');
    const helperAssignments = displayAssignments.filter((assignment) => assignment.role !== 'lead');
    const orderedAssignments = leadAssignment ? [leadAssignment, ...helperAssignments] : helperAssignments;

    return (
        <TooltipProvider>
            <div className={cn('flex items-center', className)}>
                <div className="flex -space-x-1.5">
                    {orderedAssignments.map((assignment, index) => {
                        const initials = `${assignment.user?.first_name?.[0] ?? ''}${assignment.user?.last_name?.[0] ?? ''}`;
                        const zIndex = orderedAssignments.length - index;

                        return (
                            <Tooltip key={assignment.id}>
                                <TooltipTrigger asChild>
                                    <div className="relative" style={{ zIndex }}>
                                        <Avatar className="border-border bg-muted h-7 w-7 border text-xs">
                                            <AvatarImage src={assignment.user?.avatar} />
                                            <AvatarFallback className="text-[11px] font-medium">
                                                {initials || <UserIcon className="h-3.5 w-3.5" />}
                                            </AvatarFallback>
                                        </Avatar>
                                        {assignment.role === 'lead' && (
                                            <span className="ring-background bg-warning absolute -top-0.5 -right-0.5 flex h-2.5 w-2.5 items-center justify-center rounded-full ring-2">
                                                <Crown className="text-warning-foreground h-2 w-2" />
                                            </span>
                                        )}
                                    </div>
                                </TooltipTrigger>
                                <TooltipContent>
                                    <div className="flex items-center gap-2">
                                        <span className="text-sm font-medium">
                                            {assignment.user?.first_name} {assignment.user?.last_name}
                                        </span>
                                        <span className="text-warning-subtle-foreground text-xs tracking-wide uppercase">{assignment.role}</span>
                                    </div>
                                </TooltipContent>
                            </Tooltip>
                        );
                    })}
                </div>
            </div>
        </TooltipProvider>
    );
}
